import React from 'react';
import styled from 'styled-components/macro';
import QuestionsBoxEngine from '../engine/QuestionsBoxEngine';
import OperationButtonsEngine from '../engine/OperationButtonsEngine';
import NumberButtonsEngine from '../engine/NumberButtonsEngine';
import FunctionButtonsEngine from '../engine/FunctionButtonsEngine';
import useTranslations from '../../../hooks/useTranslations';
import { Divider } from '@material-ui/core';
import _ErrorIcon from '@material-ui/icons/Error';
import _BackspaceIcon from '@material-ui/icons/Backspace';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';

const FieldCalculatorContainer = styled.div`
  display: flex;
  gap: 1rem;
`;

const FieldCalculatorLeftPanel = styled.div`
  flex: 1;
  margin: 0 2rem;
`;

const FieldCalculatorRightPanel = styled.div`
  background-color: #f7f8fc;
  flex: 1;
  padding: 0 2rem;
`;

const ValueField = styled.div`
  && {
    border: 1px solid #4c84ff;
    border-radius: 6px;
    display: flex;
    flex-wrap: wrap;
    min-height: 50px;
  }
  &&:focus-visible {
    outline: none;
  }
`;

const Description = styled.div`
  margin: 1rem 0;
`;

const ValueAndClearContainer = styled.div`
  display: flex;
  font-size: 14px;
  gap: 1rem;
  justify-content: space-between;
  margin: 1rem 0;
`;

const ValueLabel = styled.div`
  flex: 1;
`;

const ClearButton = styled.div`
  color: #4c84ff;
  cursor: pointer;
`;

const BackspaceButton = styled.div`
  cursor: pointer;
`;

const ValidationError = styled.div`
  align-items: center;
  color: #f50057;
  display: flex;
  font-size: 15px;
  margin: 1rem 0;
`;

const BlankWarning = styled.div`
  background-color: #fff2e0;
  border-left: 4px solid #feb44e;
  color: #9c5b11;
  font-size: 14px;
  margin: 0 -1.5rem;
  padding: 1rem 0 1rem 1rem;
  display: flex;
`;

const BlankWarningIcon = styled(ReportProblemOutlinedIcon)`
  && {
    font-size: 20px;
    margin-right: 0.5rem;
  }
`;

const BlankWarningText = styled.div`
  font-size: 14px;
  font-weight: 500;
  flex: 1;
`;

const ErrorIcon = styled(_ErrorIcon)`
  && {
    font-size: 15px;
    margin-right: 0.5rem;
  }
`;

const BackspaceIcon = styled(_BackspaceIcon)`
  && {
    font-size: 22px;
  }
`;

const ErrorText = styled.div``;

const FieldCalculatorView = props => {
  const t = useTranslations();
  return (
    <FieldCalculatorContainer>
      <FieldCalculatorLeftPanel>
        <Description>{t('forms.field_type.score_description')}</Description>
        <Divider />
        <ValueAndClearContainer>
          <ValueLabel>Value</ValueLabel>
          <BackspaceButton
            onClick={props.removePreviousBlock}
            onMouseDown={e => e.preventDefault()}
          >
            <BackspaceIcon />
          </BackspaceButton>
          <ClearButton onClick={props.onClickClear}>Clear all</ClearButton>
        </ValueAndClearContainer>
        <ValueField
          id="field-calculator-value-field"
          contentEditable={true}
          onInput={props.onInputValueField}
          onKeyUp={props.onKeyUpValueField}
          onKeyDown={props.onKeyDownValueField}
          onFocus={props.onFocusValueField}
          onClick={props.onFocusValueField}
        >
          {props.currentValue}
        </ValueField>
        <ValidationError>
          {props.validationError ? (
            <>
              <ErrorIcon />
              <ErrorText>
                Invalid equation.{' '}
                {t('forms.formula_error.' + props.validationError)}
              </ErrorText>
            </>
          ) : (
            ''
          )}
        </ValidationError>
        <BlankWarning>
          <BlankWarningIcon />
          <BlankWarningText>
            {t('forms.field_type.score_warning1')}
            <br />
            <br />
            {t('forms.field_type.score_warning2')}
          </BlankWarningText>
        </BlankWarning>
      </FieldCalculatorLeftPanel>
      <FieldCalculatorRightPanel>
        <QuestionsBoxEngine
          disabledButtons={props.disabledButtons}
          fields={props.fields}
          onClickField={props.onClickField}
        />
        <OperationButtonsEngine
          disabledButtons={props.disabledButtons}
          onClickButton={props.onClickButton}
        />
        <FunctionButtonsEngine
          disabledButtons={props.disabledButtons}
          onClickButton={props.onClickFunctionButton}
        />
        <NumberButtonsEngine
          disabledButtons={props.disabledButtons}
          onClickButton={props.onClickNumberButton}
        />
      </FieldCalculatorRightPanel>
    </FieldCalculatorContainer>
  );
};

export default FieldCalculatorView;
